import { useEffect, useState } from 'react';

import { useDataProvider } from '@work4all/data';
import { useSetting } from '@work4all/data/lib/settings';

import { EMailSignature } from '@work4all/models/lib/Classes/EMailSignature.entity';
import { EMailTemplate } from '@work4all/models/lib/Classes/EMailTemplate.entity';
import { EMAIL_SIGNATURE_KEYS_ARGUMENTS } from '@work4all/models/lib/constants';
import { DataRequest, Filters } from '@work4all/models/lib/DataProvider';
import { EMailTemplateKind } from '@work4all/models/lib/Enums/EMailTemplateKind.enum';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { settings } from '../../../../../../settings';

export interface UseEmailTemplaterStateOptions {
  initialTemplate?: EMailTemplate | null | undefined;
  initialSignature?: EMailSignature | null | undefined;
  additionalTemplateVariables: Record<string, unknown> | null | undefined;
  isCreateMode: boolean;
  isDraftMode: boolean;
  forceNoTemplate: boolean;
}

export type UseEmailTemplaterState = ReturnType<typeof useEmailTemplaterState>;

export function useEmailTemplaterState(options: UseEmailTemplaterStateOptions) {
  const {
    isCreateMode,
    isDraftMode,
    forceNoTemplate,
    initialTemplate = null,
    initialSignature = null,
    additionalTemplateVariables = null,
  } = options;

  const isDisabled = !isCreateMode && !isDraftMode;

  const [isLoading, setLoading] = useState(false);

  const skipInitialValues = isDisabled || forceNoTemplate || !isCreateMode;

  const standardEmailTemplates = useSetting(settings.standardEmailTemplates());
  const lastUsedEmailSignature = useSetting(settings.lastUsedEmailSignature());

  const initialTemplateId =
    standardEmailTemplates.value[EMailTemplateKind.KEINE] ?? null;
  const initialSignatureId = lastUsedEmailSignature.value?.id ?? null;

  const [template, setTemplate] = useState<EMailTemplate | null>(null);
  const [signature, setSignature] = useState<EMailSignature | null>(null);

  const [isInitialized, setIsInitialized] = useState(skipInitialValues);

  const standardTemplateResponse = useDataProvider<EMailTemplate>(
    makeTemplateRequest([{ id: { $eq: initialTemplateId } }]),
    initialTemplate != null || !initialTemplateId || isInitialized
  );

  const signatureResponse = useDataProvider<EMailSignature>(
    makeSignatureRequest(initialSignatureId),
    initialSignature != null || !initialSignatureId || isInitialized
  );

  useEffect(() => {
    if (isInitialized) {
      return;
    }

    if (standardTemplateResponse.loading || signatureResponse.loading) {
      return;
    }

    const template =
      initialTemplate ?? standardTemplateResponse.data?.[0] ?? null;
    const signature = initialSignature ?? signatureResponse.data?.[0] ?? null;

    setTemplate(template);
    setSignature(signature);
    setIsInitialized(true);
  }, [
    initialSignature,
    initialTemplate,
    isInitialized,
    signatureResponse.data,
    signatureResponse.loading,
    standardTemplateResponse.data,
    standardTemplateResponse.loading,
  ]);

  return {
    isDisabled,
    isLoading: !isInitialized || isLoading,
    setLoading,
    template,
    setTemplate,
    initialTemplate,
    signature,
    setSignature,
    variables: additionalTemplateVariables,
  };
}

const makeTemplateRequest = (filter: Filters): DataRequest => {
  const data: EMailTemplate = {
    id: null,
    name: null,
    body: null,
    eMailTemplateKind: null,
    subject: null,
    parentId: null,
    attachmentList: [{ id: null }],
    signature: {
      body: null,
      bodyTextmarkenAufgeloestInlineImagesResolved: null,
      id: null,
      name: null,
    },
    childs: [
      {
        id: null,
        languageCode: null,
        parentId: null,
        name: null,
        eMailTemplateKind: null,
        body: null,
        subject: null,
        signature: {
          body: null,
          bodyTextmarkenAufgeloestInlineImagesResolved: null,
          id: null,
          name: null,
        },
        attachmentList: [{ id: null }],
      },
    ],
  };

  return {
    entity: Entities.eMailTemplate,
    data,
    filter,
  };
};

const makeSignatureRequest = (id: string): DataRequest => {
  const data: EMailSignature = {
    id: null,
    name: null,
    body: null,
    bodyTextmarkenAufgeloestInlineImagesResolved: null,
  };

  return {
    entity: Entities.eMailSignature,
    data,
    filter: [{ id: { $eq: id } }],
    keysArguments: EMAIL_SIGNATURE_KEYS_ARGUMENTS,
  };
};
